import {
  Callout,
  Code,
  DocPage,
  DocSection,
  K,
  OL,
  P,
  PageFooter,
  PromptCard,
  UL,
} from "./_components";

export function DataTools() {
  return (
    <DocPage
      eyebrow="Building"
      title="Data tools + callTool()"
      intro={
        <>
          A widget shows information. A <strong>data tool</strong> does work —
          a lookup, a calculation, a mutation. The interesting pattern is
          combining them: a widget renders a list with a "Cancel" button, the
          button calls a data tool, the result shows up inline. That's the
          chippy-style button → action → refresh loop, built in one prompt.
        </>
      }
    >
      <DocSection id="when" title="When to reach for a data tool">
        <UL>
          <li>
            <strong>Lookups</strong>: "what's the status of order A-100?" The
            data tool returns one record from saved JSON or a fetched API.
          </li>
          <li>
            <strong>Calculations</strong>: "what's the total for this cart?"
            The data tool computes against saved data + the caller's args.
          </li>
          <li>
            <strong>Mutations</strong>: "cancel order A-100." The data tool
            represents the action — it returns a confirmation shape your widget
            renders inline.
          </li>
        </UL>
        <P>
          A data tool returns JSON only — no widget paired to it. Set{" "}
          <K>annotations: {`{ readOnlyHint: false }`}</K> if it mutates (so the
          chat host knows to confirm with the user).
        </P>
      </DocSection>

      <DocSection id="define" title="Defining a data tool">
        <P>
          The MCP shape: a name, a description (the model's router — be
          specific), an input JSON schema, an optional saved <K>data</K> blob,
          and optional annotations.
        </P>
        <PromptCard
          title="Add a data tool to an existing project"
          prompt={`Use project add_data_tool on my "Orders" project. Tool slug: "cancel-order". Description: "Cancel an order by its id. Returns {ok, orderId, cancelledAt}." inputSchema { type:"object", properties:{ orderId:{type:"string"} }, required:["orderId"] }. annotations { readOnlyHint: false }. data { ok: true, cancelledAt: "2026-05-27T19:00:00Z" }. triggerPhrases ["cancel order A-100","stop the Alice order"].`}
          note={
            <>
              The returned JSON is the saved <K>data</K> shallow-merged with the
              caller's arguments. So calling{" "}
              <K>{`cancel-order({ orderId: "A-100" })`}</K> returns{" "}
              <K>{`{ ok: true, cancelledAt: "...", orderId: "A-100" }`}</K>.
            </>
          }
        />
      </DocSection>

      <DocSection id="call" title="Calling a data tool from a widget — callTool()">
        <P>
          Widgets get a single primitive for invoking other tools on the same
          project: <K>callTool(name, args)</K>. The host routes the call back
          to the project endpoint, the data tool runs, and the JSON returns to
          your widget. Wire it to a button's onClick.
        </P>
        <Code>{`import { useState } from "react";
import { useToolOutput, callTool } from "@martzmakes/widget";

export default function OrdersTable() {
  const { rows = [] } = useToolOutput();
  const [results, setResults] = useState({}); // orderId -> response

  return (
    <table>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{r.id}</td>
            <td>{r.customer}</td>
            <td>{results[r.id] ? "cancelled" : r.status}</td>
            <td>
              <button
                disabled={!!results[r.id]}
                onClick={async () => {
                  const res = await callTool("cancel-order", { orderId: r.id });
                  setResults((m) => ({ ...m, [r.id]: res }));
                }}
              >
                {results[r.id] ? "✓" : "Cancel"}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}`}</Code>
        <Callout kind="note" title="callTool only works inside a host.">
          When you're previewing a widget on the project page directly, there's
          no host — clicking the button will throw "callTool unavailable outside
          host". That's expected. The button works in Claude / ChatGPT / on a
          published share page where the host bridge is wired up.
        </Callout>
      </DocSection>

      <DocSection id="end-to-end" title="End-to-end Orders example in one prompt">
        <P>
          The full pattern (data tool + UI widget that calls it) fits in one
          paste. This is the example we ship as a built-in on Settings:
        </P>
        <PromptCard
          title="Build the Orders project from a single prompt"
          prompt={`Build a project called "Orders".

1. First, use project add_data_tool to create "cancel-order" with description "Cancel an order by its id. Returns {ok, orderId, cancelledAt}." inputSchema { type:"object", properties:{ orderId:{type:"string"} }, required:["orderId"] }, annotations { readOnlyHint: false }, data { ok: true, cancelledAt: "2026-05-27T19:00:00Z" }, triggerPhrases ["cancel order A-100","stop the Alice order"].

2. Then use dynamic_widget with projectName: "Orders" and toolSlug: "orders-table" to render an orders table. Pass data: { rows: [{id:"A-100",customer:"Alice",status:"open"},{id:"A-101",customer:"Bob",status:"open"}] }. Each row gets a "Cancel" button that calls await callTool('cancel-order', { orderId: row.id }) and shows the result inline.

3. Return the project's MCP URL and the public share link.`}
          note={
            <>
              When you connect <K>mcpbuild.app/mcp/orders</K> in a chat client,
              the AI will pick <K>cancel-order</K> when someone says any of the
              trigger phrases — no need to mention the tool name.
            </>
          }
        />
      </DocSection>

      <DocSection id="schemas" title="A good input schema is the difference between work and frustration">
        <P>
          The data tool's input schema doubles as the model's router. If you
          tell the AI to write <K>inputSchema: {`{ type: "object" }`}</K> with
          no properties, the chat host will pass garbage args; if you write a
          tight schema with <K>required</K> fields, it can't get it wrong.
        </P>
        <P>Rule of thumb:</P>
        <OL>
          <li>List every field the tool genuinely needs in <K>properties</K>.</li>
          <li>
            Put fields the model <strong>must</strong> supply in{" "}
            <K>required</K>.
          </li>
          <li>
            Use <K>type</K> + <K>enum</K> where you can — a typed field with an
            enum gets routed correctly far more often than a free-form string.
          </li>
        </OL>
      </DocSection>

      <PageFooter
        prev={{ to: "/docs/widgets", label: "Building widgets" }}
        next={{ to: "/docs/triggers", label: "Trigger phrases" }}
      />
    </DocPage>
  );
}
